import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Building2, ArrowLeft, CheckCircle, User, Crown, Loader2, Check, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Logo from '@/components/Logo';
import AddressInput from '@/components/ui/AddressInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ClinicSignupEnhanced() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [subdomainChecking, setSubdomainChecking] = useState(false);
  const [subdomainAvailable, setSubdomainAvailable] = useState(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    clinic_name: '',
    subdomain: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    admin_name: '',
    admin_email: '',
    admin_password: '',
    confirmPassword: '',
    subscription_tier: 'professional'
  });

  useEffect(() => {
    fetchSubscriptionTiers();
  }, []);

  const fetchSubscriptionTiers = async () => {
    try {
      const response = await axios.get(`${API}/public/clinic-registration/subscription-tiers`);
      setTiers(response.data.tiers);
    } catch (error) {
      console.error('Failed to fetch tiers:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'clinic_name' && !formData.subdomain) {
      const slug = value.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .substring(0, 20);
      setFormData(prev => ({ ...prev, subdomain: slug }));
    }
  };

  const checkSubdomainAvailability = async (subdomain) => {
    if (!subdomain || subdomain.length < 3) {
      setSubdomainAvailable(null);
      return;
    }

    setSubdomainChecking(true);
    try {
      const response = await axios.get(`${API}/public/clinic-registration/check-subdomain/${subdomain}`);
      setSubdomainAvailable(response.data.available);
    } catch (error) {
      setSubdomainAvailable(false);
    } finally {
      setSubdomainChecking(false);
    }
  };

  const handleSubdomainChange = (e) => {
    const value = e.target.value.toLowerCase();
    setFormData(prev => ({ ...prev, subdomain: value }));
    setSubdomainAvailable(null);
    
    if (value.length >= 3) {
      setTimeout(() => checkSubdomainAvailability(value), 500);
    }
  };

  const handleStep1Submit = (e) => {
    e.preventDefault();
    
    if (!formData.clinic_name || !formData.subdomain || !formData.contact_email) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (subdomainAvailable === false) {
      toast.error('Please choose an available subdomain');
      return;
    }

    setStep(2);
  };

  const handleStep2Submit = (e) => {
    e.preventDefault();
    setStep(3);
  };

  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.admin_name || !formData.admin_email || !formData.admin_password) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.admin_password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.admin_password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/public/clinic-registration/register`, {
        clinic_name: formData.clinic_name,
        subdomain: formData.subdomain,
        contact_email: formData.contact_email,
        contact_phone: formData.contact_phone,
        address: formData.address,
        admin_name: formData.admin_name,
        admin_email: formData.admin_email,
        admin_password: formData.admin_password,
        subscription_tier: formData.subscription_tier
      });

      toast.success(
        <div>
          <p className="font-semibold">🎉 Welcome to MyMedMember by Clinicax Systems!</p>
          <p className="text-sm">{response.data.message}</p>
        </div>
      );
      
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            message: 'Your clinic has been created! Please login with your admin credentials.',
            email: formData.admin_email 
          }
        });
      }, 2000);

    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create clinic. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center text-teal-600 hover:text-teal-700 mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex justify-center mb-4">
            <Logo size="lg" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Start Your Free Trial</h1>
          <p className="text-gray-600">30-day free trial • No credit card required • Cancel anytime</p>
        </div>

        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center">
            {[1, 2, 3].map((stepNum) => (
              <div key={stepNum} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                  step >= stepNum ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {step > stepNum ? <CheckCircle className="w-5 h-5" /> : stepNum}
                </div>
                {stepNum < 3 && (
                  <div className={`w-24 h-1 ${step > stepNum ? 'bg-teal-600' : 'bg-gray-200'}`}></div>
                )}
              </div>
            ))}
          </div>
        </div>

        <Card className="shadow-xl">
          {step === 1 && (
            <>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Building2 className="w-6 h-6 mr-2 text-teal-600" />
                  Clinic Information
                </CardTitle>
                <CardDescription>Tell us about your clinic</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleStep1Submit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="clinic_name">Clinic Name *</Label>
                    <Input
                      id="clinic_name"
                      name="clinic_name"
                      value={formData.clinic_name}
                      onChange={handleChange}
                      placeholder="e.g., Wellness Medical Center"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subdomain">Your Clinic URL *</Label>
                    <div className="flex items-center">
                      <Input
                        id="subdomain"
                        name="subdomain"
                        value={formData.subdomain}
                        onChange={handleSubdomainChange}
                        placeholder="e.g., wellness"
                        required
                        className="rounded-r-none"
                      />
                      <span className="px-3 py-2 bg-gray-100 border border-l-0 rounded-r-md text-sm text-gray-600">
                        .mymedmember.com
                      </span>
                    </div>
                    {subdomainChecking && (
                      <p className="text-xs text-gray-500 flex items-center">
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        Checking availability...
                      </p>
                    )}
                    {subdomainAvailable === true && (
                      <p className="text-xs text-green-600 flex items-center">
                        <Check className="w-3 h-3 mr-1" />
                        Subdomain is available!
                      </p>
                    )}
                    {subdomainAvailable === false && (
                      <p className="text-xs text-red-600 flex items-center">
                        <X className="w-3 h-3 mr-1" />
                        Subdomain is already taken
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contact_email">Contact Email *</Label>
                      <Input
                        id="contact_email"
                        name="contact_email"
                        type="email"
                        value={formData.contact_email}
                        onChange={handleChange}
                        placeholder="contact@clinic.com"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="contact_phone">Phone Number</Label>
                      <Input
                        id="contact_phone"
                        name="contact_phone"
                        type="tel"
                        value={formData.contact_phone}
                        onChange={handleChange}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>

                  <AddressInput
                    label="Clinic Address"
                    value={formData.address}
                    onChange={handleChange}
                    onAddressSelected={(data) => {
                      setFormData(prev => ({
                        ...prev,
                        address: data.formatted_address
                      }));
                    }}
                    placeholder="123 Main St, City, State ZIP"
                    multiline
                  />

                  <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700" size="lg">
                    Continue to Plan Selection
                  </Button>
                </form>
              </CardContent>
            </>
          )}

          {step === 2 && (
            <>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Crown className="w-6 h-6 mr-2 text-teal-600" />
                  Choose Your Plan
                </CardTitle>
                <CardDescription>Select the plan that fits your needs • Start with 30-day free trial</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleStep2Submit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {tiers.map((tier) => (
                      <Card
                        key={tier.id}
                        className={`cursor-pointer transition-all ${
                          formData.subscription_tier === tier.id
                            ? 'ring-2 ring-teal-500 border-teal-500'
                            : 'hover:border-gray-400'
                        } ${tier.popular ? 'border-2 border-orange-400' : ''}`}
                        onClick={() => setFormData(prev => ({ ...prev, subscription_tier: tier.id }))}
                      >
                        {tier.popular && (
                          <div className="bg-orange-500 text-white text-xs font-semibold text-center py-1">
                            MOST POPULAR
                          </div>
                        )}
                        <CardContent className="pt-6">
                          <div className="text-center mb-4">
                            <h3 className="text-xl font-bold mb-2">{tier.name}</h3>
                            <div className="text-3xl font-bold text-teal-600 mb-1">
                              ${tier.price}
                            </div>
                            <p className="text-sm text-gray-500">per month</p>
                          </div>
                          <p className="text-sm text-gray-600 mb-4">{tier.description}</p>
                          <div className="space-y-2">
                            {tier.features.slice(0, 5).map((feature, idx) => (
                              <div key={idx} className="flex items-start text-sm">
                                <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                                <span className="text-gray-700">{feature}</span>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
                    <p className="font-semibold mb-1">30-Day Free Trial Included</p>
                    <p>Start your free trial now. No credit card required. You can cancel anytime during the trial.</p>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      type="button"
                      onClick={() => setStep(1)}
                      variant="outline"
                      className="flex-1"
                    >
                      Back
                    </Button>
                    <Button type="submit" className="flex-1 bg-teal-600 hover:bg-teal-700" size="lg">
                      Continue to Admin Setup
                    </Button>
                  </div>
                </form>
              </CardContent>
            </>
          )}

          {step === 3 && (
            <>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <User className="w-6 h-6 mr-2 text-teal-600" />
                  Create Admin Account
                </CardTitle>
                <CardDescription>Set up your administrator account</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleFinalSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="admin_name">Full Name *</Label>
                    <Input
                      id="admin_name"
                      name="admin_name"
                      value={formData.admin_name}
                      onChange={handleChange}
                      placeholder="Dr. John Smith"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="admin_email">Admin Email *</Label>
                    <Input
                      id="admin_email"
                      name="admin_email"
                      type="email"
                      value={formData.admin_email}
                      onChange={handleChange}
                      placeholder="admin@clinic.com"
                      required
                    />
                    <p className="text-xs text-gray-500">You'll use this email to login</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="admin_password">Password *</Label>
                    <Input
                      id="admin_password"
                      name="admin_password"
                      type="password"
                      value={formData.admin_password}
                      onChange={handleChange}
                      placeholder="Minimum 8 characters"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password *</Label>
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Re-enter password"
                      required
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      onClick={() => setStep(2)}
                      variant="outline"
                      className="flex-1"
                      disabled={loading}
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700"
                      size="lg"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Creating Your Clinic...
                        </>
                      ) : (
                        'Complete Registration'
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </>
          )}
        </Card>

        <p className="text-center text-sm text-gray-500 mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-teal-600 hover:text-teal-700 font-medium">
            Login here
          </Link>
        </p>
        
        <p className="text-center text-sm text-gray-500 mt-2">
          Are you a patient?{' '}
          <Link to="/register-patient" className="text-teal-600 hover:text-teal-700 font-medium">
            Register as a member
          </Link>
        </p>
      </div>
    </div>
  );
}

export default ClinicSignupEnhanced;
